import { RefObject, useCallback, useMemo, useState } from "react";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { canNestGroupUnderParent } from "@common/constants/domains/accounting/accountGroups";
import { AccountGroup } from "@common/types/domains/accounting/account";
import { ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { EditableAccountPatch, ScopeInsertPosition } from "../draftOperations";
import { DraftGroup } from "../draftState";
import { findGroupInScope } from "../draftGroupOperations";
import { NestedAddAccountModalProps } from "./NestedAddAccountModal";
import { NestedAddGroupModalProps } from "./NestedAddGroupModal";
import { NestedEditorRow } from "./nestedRowTypes";
import {
    AddFanOutTarget,
    resolveAddFanOut,
    resolveRowAddScope,
    resolveTypeHeaderAddScope,
} from "./nestedAddScope";
import { NestedAddScope } from "./nestedTableMeta";
import { nestedRowsToPickerGroups } from "./nestedRowsToPickerGroups";
import { NESTED_GROUP_DEPTH_LIMIT_MESSAGE } from "./useNestedEditorAddActions";
import {
    NestedFooterAddSelection,
    NestedFooterCreateAccountParams,
    NestedFooterCreateGroupParams,
} from "./nestedFooterAddTypes";
import { CoaEditorCommand } from "./coaEditorCommand";

const DUPLICATE_GROUP_NAME_MESSAGE = "A group with this name already exists";

interface UseNestedFooterAddModalsParams {
    tableRef: RefObject<ViewTableHandle | null>;
    nestedRows: NestedEditorRow[];
    // Keyed by row id, not constituent id.
    rowById: Map<string, NestedEditorRow>;
    displayedEntityIds: number[] | undefined;
    groups: DraftGroup[];
    addAccountInScope: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        position?: ScopeInsertPosition;
        codeText: string | null;
    }) => string;
    suggestCreateCodeText: (params: {
        type: AccountType;
        targets: Array<{ entityId: number; parentGroupId: string | null }>;
    }) => string | null;
    updateAccount: (id: string, patch: EditableAccountPatch) => void;
    createGroup: (params: {
        entityId: number;
        type: AccountType;
        parentGroupId: string | null;
        name: string;
        position?: ScopeInsertPosition;
    }) => string;
    onOperation?: (command: CoaEditorCommand) => void;
}

interface NestedFooterAddModals {
    openAddAccount: (selection: NestedFooterAddSelection) => void;
    openAddGroup: (selection: NestedFooterAddSelection) => void;
    // True while either footer placement modal is open. The footer disables its
    // Save ⌘↵ / ⌥A / ⌥G hotkeys while this is true so ⌘↵ resolves to the modal's
    // Create only (the modal and the footer Save share the mod+enter chord).
    isAddModalOpen: boolean;
    addAccountModalProps: Omit<NestedAddAccountModalProps, "groups">;
    addGroupModalProps: Omit<NestedAddGroupModalProps, "groups">;
}

const APPEND: ScopeInsertPosition = { kind: "append" };

export const useNestedFooterAddModals = ({
    tableRef,
    nestedRows,
    rowById,
    displayedEntityIds,
    groups,
    addAccountInScope,
    suggestCreateCodeText,
    updateAccount,
    createGroup,
    onOperation,
}: UseNestedFooterAddModalsParams): NestedFooterAddModals => {
    const { error: errorToast } = useToaster();
    const [addAccountSelection, setAddAccountSelection] =
        useState<NestedFooterAddSelection | null>(null);
    const [addGroupSelection, setAddGroupSelection] =
        useState<NestedFooterAddSelection | null>(null);

    const pickerGroups = useMemo<AccountGroup[]>(
        () => nestedRowsToPickerGroups(nestedRows),
        [nestedRows],
    );

    const resolveScope = useCallback(
        ({ type, parentRowId }: NestedFooterAddSelection): NestedAddScope => {
            const row =
                parentRowId != null ? rowById.get(parentRowId) : undefined;
            if (!row || row.kind === "type") {
                return resolveTypeHeaderAddScope(type, displayedEntityIds);
            }
            return (
                resolveRowAddScope(row, displayedEntityIds) ??
                resolveTypeHeaderAddScope(type, displayedEntityIds)
            );
        },
        [rowById, displayedEntityIds],
    );

    const resolveFanOutTargets = useCallback(
        (selection: NestedFooterAddSelection): AddFanOutTarget[] =>
            resolveAddFanOut(resolveScope(selection)).map((target) => ({
                ...target,
                position: APPEND,
            })),
        [resolveScope],
    );

    const openAddAccount = useCallback(
        (selection: NestedFooterAddSelection) =>
            setAddAccountSelection(selection),
        [],
    );
    const openAddGroup = useCallback(
        (selection: NestedFooterAddSelection) =>
            setAddGroupSelection(selection),
        [],
    );

    const closeAddAccount = useCallback(() => setAddAccountSelection(null), []);
    const closeAddGroup = useCallback(() => setAddGroupSelection(null), []);

    const handleCreateAccount = useCallback(
        ({ type, parentRowId, name }: NestedFooterCreateAccountParams) => {
            const scope = resolveScope({ type, parentRowId });
            const targets = resolveFanOutTargets({ type, parentRowId });
            if (targets.length === 0) {
                setAddAccountSelection(null);
                return;
            }
            const codeText = suggestCreateCodeText({
                type: scope.type,
                targets,
            });
            const created = targets.map((target) => {
                const accountId = addAccountInScope({
                    entityId: target.entityId,
                    type: scope.type,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                    codeText,
                });
                updateAccount(accountId, { name });
                return {
                    accountId,
                    entityId: target.entityId,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                };
            });
            onOperation?.({
                type: "addAccount",
                created,
                accountType: scope.type,
                name,
            });
            tableRef.current?.expandRowAndAncestors(scope.hostRowId);
            setAddAccountSelection(null);
        },
        [
            addAccountInScope,
            suggestCreateCodeText,
            onOperation,
            resolveFanOutTargets,
            resolveScope,
            tableRef,
            updateAccount,
        ],
    );

    const handleCreateGroup = useCallback(
        ({ type, parentRowId, name }: NestedFooterCreateGroupParams) => {
            const scope = resolveScope({ type, parentRowId });
            const targets = resolveFanOutTargets({ type, parentRowId });
            if (targets.length === 0) {
                setAddGroupSelection(null);
                return;
            }
            const hasDuplicate = targets.some(
                (target) =>
                    findGroupInScope({
                        groups,
                        entityId: target.entityId,
                        type: scope.type,
                        parentGroupId: target.parentGroupId,
                        name,
                    }) !== undefined,
            );
            if (hasDuplicate) {
                errorToast(DUPLICATE_GROUP_NAME_MESSAGE);
                return;
            }
            const hasDepthViolation = targets.some(
                (target) =>
                    !canNestGroupUnderParent({
                        groups,
                        parentGroupId: target.parentGroupId,
                    }),
            );
            if (hasDepthViolation) {
                errorToast(NESTED_GROUP_DEPTH_LIMIT_MESSAGE);
                return;
            }
            const created = targets.map((target) => {
                const groupId = createGroup({
                    entityId: target.entityId,
                    type: scope.type,
                    parentGroupId: target.parentGroupId,
                    name,
                    position: target.position,
                });
                return {
                    groupId,
                    entityId: target.entityId,
                    parentGroupId: target.parentGroupId,
                    position: target.position,
                };
            });
            onOperation?.({
                type: "addGroup",
                created,
                accountType: scope.type,
                name,
            });
            tableRef.current?.expandRowAndAncestors(scope.hostRowId);
            setAddGroupSelection(null);
        },
        [
            createGroup,
            errorToast,
            groups,
            onOperation,
            resolveFanOutTargets,
            resolveScope,
            tableRef,
        ],
    );

    return {
        openAddAccount,
        openAddGroup,
        isAddModalOpen:
            addAccountSelection != null || addGroupSelection != null,
        addAccountModalProps: {
            isOpen: addAccountSelection != null,
            pickerGroups,
            resolveFanOutTargets,
            defaultSelection: addAccountSelection,
            onCancel: closeAddAccount,
            onCreate: handleCreateAccount,
        },
        addGroupModalProps: {
            isOpen: addGroupSelection != null,
            pickerGroups,
            resolveFanOutTargets,
            defaultSelection: addGroupSelection,
            onCancel: closeAddGroup,
            onCreate: handleCreateGroup,
        },
    };
};
